// Imports for external libraries go here.
import dynamic from 'next/dynamic';
import { FC, memo, useCallback, useMemo } from 'react';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Accordion, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { CardComparisonAccordionProps, CategoryItem } from './CardComparisonAccordion.types';
import { StyledCardComparisonAccordion } from './CardComparisonAccordion.styles';
import { usePageContext } from '../../context/PageContext';
import { setCardArray } from '../../modules/utils';

const TableRow = dynamic(() => import('../../molecules/DataCellComponent').then(mod => mod.TableRow));

export const CardComparisonAccordionConfig = {
  emptyLabel: 'CardComparison',
  isEmpty: () => true,
  resourceType: `/components/content/CardComparison`,
};

// Use named rather than default exports.
export const CardComparisonAccordion: FC<CardComparisonAccordionProps> = memo((props: CardComparisonAccordionProps) => {
  const isViewportL = useCheckBreakpoint('viewportL');
  const { categoriesToBeShown, group, styleClass } = props;
  const { cardtype1, cardtype2, cardtype3 } = usePageContext();

  const cardIdFromURL = useMemo(
    () => [cardtype1, cardtype2, cardtype3].filter(Boolean),
    [cardtype1, cardtype2, cardtype3]
  );

  const cardData = useMemo(() => setCardArray(props?.pageItems), [props?.pageItems]);

  const filteredData = useMemo(
    () =>
      cardData
        .filter(data => (data.cardId ? cardIdFromURL?.includes(data.cardId) : false))
        .map(data => ({ ...data, cardType: data.cardType ?? 'none' })),
    [cardData, cardIdFromURL]
  );

  const getDataList = useCallback(
    (category: CategoryItem) => {
      const data = filteredData.filter(item => item.categoryId === category.categoryId);
      if (cardIdFromURL?.length) {
        data.sort((a, b) => cardIdFromURL.indexOf(a.cardId!) - cardIdFromURL.indexOf(b.cardId!));
      }
      return data;
    },
    [filteredData, cardIdFromURL]
  );

  return (
    <StyledCardComparisonAccordion
      data-component-name="m-cobrand-CardComparisonAccordion"
      data-testid="cobrand-CardComparisonAccordion"
      className={styleClass}
    >
      <Accordion
        id={group.id}
        headerChildren={group.value}
        styleclass="accordion-container"
        isOpen={!isViewportL ? group.count <= 1 : true}
      >
        {categoriesToBeShown.map(category => {
          const dataList = getDataList(category);
          return (
            <div key={category.categoryId ?? category.groupId ?? category.categoryHeading}>
              <TableRow category={category} dataList={dataList} cardIdFromURL={cardIdFromURL} />
            </div>
          );
        })}
      </Accordion>
    </StyledCardComparisonAccordion>
  );
});
